'use client'

import { useState, useEffect } from 'react'
import { useCurrency } from '@/lib/currency'
import { landlordAccountApi, payoutApi, commissionApi } from '@/lib/api'
import type { LandlordAccount, PayoutRequest, EarningsBreakdown } from '@/types'
import { DollarSign, TrendingUp, CreditCard, AlertCircle, Wallet, Percent, ArrowDownCircle } from 'lucide-react'
import PayoutRequestModal from './PayoutRequestModal'
import PayoutHistory from './PayoutHistory'

interface EarningsDashboardProps {
  onUpdate?: () => void
}

export default function EarningsDashboard({ onUpdate }: EarningsDashboardProps) {
  const { format } = useCurrency()
  const [loading, setLoading] = useState(true)
  const [balance, setBalance] = useState<LandlordAccount | null>(null)
  const [earnings, setEarnings] = useState<EarningsBreakdown | null>(null)
  const [commissionRate, setCommissionRate] = useState<number>(0.10)
  const [showPayoutModal, setShowPayoutModal] = useState(false)
  const [payoutRequests, setPayoutRequests] = useState<PayoutRequest[]>([])
  const [dateRange, setDateRange] = useState<'all' | 'month' | 'year'>('all')

  const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null

  // Load balance data
  const loadBalance = async () => {
    if (!token) return
    
    try {
      const response = await landlordAccountApi.getBalance(token)
      if (response.success && response.data) {
        // API returns data directly, not wrapped
        const data = (response.data as any).data || response.data
        setBalance(data as any)
      }
    } catch (error) {
      console.error('Error loading balance:', error)
    }
  }

  // Load earnings data
  const loadEarnings = async () => {
    if (!token) return
    
    try {
      const now = new Date()
      let startDate: string | undefined
      
      if (dateRange === 'month') {
        startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString()
      } else if (dateRange === 'year') {
        startDate = new Date(now.getFullYear(), 0, 1).toISOString()
      }
      
      const response = await landlordAccountApi.getEarnings(token, {
        startDate,
        limit: 100,
        page: 1
      })
      
      if (response.success && response.data) {
        setEarnings((response.data as any).breakdown)
      }
    } catch (error) {
      console.error('Error loading earnings:', error)
    }
  }

  // Load commission rate
  const loadCommissionRate = async () => {
    if (!token) return
    
    try {
      const response = await commissionApi.getRate(token)
      if (response.success && response.data) {
        setCommissionRate((response.data as any).commissionRate || 0.10)
      }
    } catch (error) {
      console.error('Error loading commission rate:', error)
    }
  }

  // Load payout requests
  const loadPayoutRequests = async () => {
    if (!token) return
    
    try {
      const response = await payoutApi.getRequests(token)
      if (response.success && response.data) {
        setPayoutRequests((response.data as any).payoutRequests || [])
      }
    } catch (error) {
      console.error('Error loading payout requests:', error)
    }
  }

  // Load all data
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true)
      await Promise.all([
        loadBalance(),
        loadEarnings(),
        loadCommissionRate(),
        loadPayoutRequests()
      ])
      setLoading(false)
    }
    
    loadAll()
  }, [dateRange])

  // Reload when payout is created
  const handlePayoutCreated = () => {
    setShowPayoutModal(false)
    loadBalance()
    loadPayoutRequests()
    onUpdate?.()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        <span className="ml-3 text-gray-600">Loading earnings...</span>
      </div>
    )
  }

  const canRequestPayout = balance && 
    balance.availableBalance >= 50000 && 
    balance.kycVerified &&
    balance.accountStatus === 'active'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Earnings & Payouts</h1>
          <p className="text-gray-600 mt-1">Track your earnings and request payouts</p>
        </div>
        {canRequestPayout && (
          <button
            onClick={() => setShowPayoutModal(true)}
            className="px-6 py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-colors flex items-center gap-2"
          >
            <ArrowDownCircle className="w-5 h-5" />
            Request Payout
          </button>
        )}
      </div>

      {/* Balance Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Available Balance - what landlord can withdraw (after commission) */}
        <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-6 ring-2 ring-emerald-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-600">Available to withdraw</h3>
            <Wallet className="w-5 h-5 text-emerald-600" />
          </div>
          <p className="text-3xl font-bold text-emerald-700">{format(balance?.availableBalance || 0)}</p>
          <p className="text-xs text-gray-500 mt-1">Platform commission already deducted. Request a payout anytime.</p>
        </div>

        {/* Pending Balance */}
        <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-600">Pending Balance</h3>
            <CreditCard className="w-5 h-5 text-amber-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{format(balance?.pendingBalance || 0)}</p>
          <p className="text-xs text-gray-500 mt-1">In escrow (after commission)</p>
        </div>

        {/* Total Gross Earnings */}
        <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-600">Total Gross Earnings</h3>
            <DollarSign className="w-5 h-5 text-blue-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{format(balance?.totalGrossEarnings || 0)}</p>
          <p className="text-xs text-gray-500 mt-1">Before commission</p>
        </div>

        {/* Total Commission Deducted */}
        <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-600">Commission deducted</h3>
            <Percent className="w-5 h-5 text-amber-600" />
          </div>
          <p className="text-3xl font-bold text-amber-700">{format(balance?.totalCommissionPaid || 0)}</p>
          <p className="text-xs text-gray-500 mt-1">
            {(commissionRate * 100).toFixed(1)}% platform fee applied when rent is released from escrow
          </p>
        </div>

        {/* Total Net Earnings */}
        <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-600">Total Net Earnings</h3>
            <TrendingUp className="w-5 h-5 text-green-600" />
          </div>
          <p className="text-3xl font-bold text-green-600">{format(balance?.totalNetEarnings || 0)}</p>
          <p className="text-xs text-gray-500 mt-1">After commission</p>
        </div>

        {/* Total Payouts */}
        <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-600">Total Payouts</h3>
            <ArrowDownCircle className="w-5 h-5 text-purple-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{format(balance?.totalPayouts || 0)}</p>
          <p className="text-xs text-gray-500 mt-1">All-time payouts</p>
        </div>
      </div>

      {/* Commission Summary */}
      <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Commission Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Current Commission Rate</p>
            <p className="text-2xl font-bold text-blue-600">{(commissionRate * 100).toFixed(1)}%</p>
          </div>
          <div className="bg-red-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Commission Paid (Selected Period)</p>
            <p className="text-2xl font-bold text-red-600">{format(earnings?.totalCommissionPaid || 0)}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Net Earnings (Selected Period)</p>
            <p className="text-2xl font-bold text-green-600">{format(earnings?.totalNetEarnings || 0)}</p>
          </div>
        </div>
        
        {/* Visual representation */}
        {earnings && earnings.totalGrossEarnings > 0 && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Breakdown</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setDateRange('all')}
                  className={`px-3 py-1 text-xs rounded ${dateRange === 'all' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                >
                  All Time
                </button>
                <button
                  onClick={() => setDateRange('year')}
                  className={`px-3 py-1 text-xs rounded ${dateRange === 'year' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                >
                  This Year
                </button>
                <button
                  onClick={() => setDateRange('month')}
                  className={`px-3 py-1 text-xs rounded ${dateRange === 'month' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                >
                  This Month
                </button>
              </div>
            </div>
            <div className="h-8 bg-gray-200 rounded-lg overflow-hidden flex">
              <div
                className="bg-green-600 h-full flex items-center justify-center text-white text-xs font-medium"
                style={{ width: `${(earnings.totalNetEarnings / earnings.totalGrossEarnings) * 100}%` }}
              >
                Net: {format(earnings.totalNetEarnings)}
              </div>
              <div
                className="bg-red-600 h-full flex items-center justify-center text-white text-xs font-medium"
                style={{ width: `${(earnings.totalCommissionPaid / earnings.totalGrossEarnings) * 100}%` }}
              >
                Commission: {format(earnings.totalCommissionPaid)}
              </div>
            </div>
            <div className="flex items-center justify-between mt-2 text-xs text-gray-600">
              <span>Total: {format(earnings.totalGrossEarnings)}</span>
              <span>{earnings.paymentCount} payments</span>
            </div>
          </div>
        )}
      </div>

      {/* KYC Verification Warning */}
      {balance && !balance.kycVerified && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-amber-900">KYC Verification Required</h3>
            <p className="text-sm text-amber-700 mt-1">
              Please complete KYC verification to request payouts.
            </p>
          </div>
        </div>
      )}

      {/* Payout Request Warning */}
      {balance && balance.availableBalance < 50000 && balance.availableBalance > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-900">Minimum Payout Amount</h3>
            <p className="text-sm text-blue-700 mt-1">
              Minimum payout amount is ₦50,000. Your available balance is {format(balance.availableBalance)}.
            </p>
          </div>
        </div>
      )}

      {/* Recent Transactions */}
      {earnings && earnings.payments && earnings.payments.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Recent Transactions</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gross Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Commission</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net Amount</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {earnings.payments.slice(0, 10).map((payment) => (
                  <tr key={payment.id}>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {new Date(payment.date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {format(payment.amount)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-red-600">
                      {format(payment.commissionAmount)} ({(payment.commissionRate * 100).toFixed(1)}%)
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-green-600">
                      {format(payment.netAmount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Payout History */}
      <PayoutHistory payoutRequests={payoutRequests} onRefresh={loadPayoutRequests} />

      {/* Payout Request Modal */}
      {showPayoutModal && balance && (
        <PayoutRequestModal
          availableBalance={balance.availableBalance}
          kycVerified={balance.kycVerified}
          onClose={() => setShowPayoutModal(false)}
          onSuccess={handlePayoutCreated}
        />
      )}
    </div>
  )
}

